import React from 'react';
import {
    Button, ButtonGroup, Spinner, Collapse,
    FormGroup,
    Container,
    UncontrolledTooltip,
} from 'reactstrap';
import { v7 as uuid } from 'uuid';
import cls from 'classnames';
import { isEqual } from 'lodash-es';
import {
    faFileContract, faSave, faTimes, faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FlexCenterRow, FlexEndRow, FormBlocker, SimpleTable, SmallButton, SubHeading, toasty, YesNoBadge, onReactSelectChanged,
    Input,
    onYesNoChanged,
    IconButton,
    FormCheckbox,
    onFieldChange,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, {
    ApiRoutes, ApplicationPermissions, AppNavPaths, BillingCycles, ChargeTypeResourceTypes,
} from '../Common';
import {
    WorkOrderNumberOptions, ChargeTypeEligbility, ChargeTypeEligibilities,
    DayOfWeekSelect,
    DayOfMonthSelect,
    getWorkCardOptions,
    WarningThresholdOptions,
} from '../contracts/Contract';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import NumericInput from '../common/forms/NumericInput';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ChargeTypeSelect from '../chargeType/ChargeTypeSelect';
import ReorderListWidget from '../widgets/ReorderListWidget';
import SlideForm from '../common/forms/SlideForm';
import authService from '../api-authorization/AuthorizeService';
import { _ContractChargeTypeEligibility } from '../chargeType/ChargeType';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { TipAlert } from '../common/TipAlert';
import { Card } from '../common/Card';
import { ContractBumpRuleGrid } from '../contracts/ContractBumpRuleGrid';
import { Modal } from '../common/Modal';
import ChargeTypeGroupSelect from '../chargeTypeGroup/ChargeTypeGroupSelect';
import { ContractPayrollIncentiveGrid } from '../contracts/ContractPayrollIncentiveGrid';
import PayScheduleSelect from '../paySchedule/PayScheduleSelect';
import USCountyGroupSelect from '../uscounties/USCountyGroupSelect';
import { ContractTemplate } from './ContractTemplate';

class ContractTemplateForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.chargeTypeSelectorRef = React.createRef();
        this.chargeTypeGroupSelectorRef = React.createRef();
        this.payScheduleSelectorRef = React.createRef();
        this.uSCountyGroupSelectorRef = React.createRef();
        this.chargeTypesWidgetRef = React.createRef();

        const stateBase = {
            contractTemplate: new ContractTemplate(),
            isNew: true,
            loading: true,
            originalData: null,
            selectedChargeType: null,
            selectedSortableChargeTypes: [],
            showReorderWidget: false,
            contractUnionWorkTypes: [],
            contractScheduleTypes: [],
            billingCycles: [],
            operationalCategories: [],
            employeeGroups: [],
            certifications: [],
            jobTypes: [],
            ...new BaseFormViewModel(),
        };

        this.state = { ...stateBase };
        this.onSubmit = this.onSubmit.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onYesNoChanged = this.onYesNoChanged.bind(this);
        this.handleIncentivesChange = this.handleIncentivesChange.bind(this);
    }

    // #region LIFECYCLE EVENTS
    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    // #endregion

    // #region CHARGE TYPES
    onAddChargeTypeGroups = () => {
        const { contractTemplate } = { ...this.state };
        const { chargeTypeGroups } = { ...contractTemplate };
        const typesToOmit = (chargeTypeGroups ?? []).map((c) => c.chargeTypeGroupId) ?? [];
        // pass in types already selected
        this.chargeTypeGroupSelectorRef.current.open([...typesToOmit], contractTemplate.isProjectContract);
    };

    onAddChargeTypes = () => {
        const { contractTemplate } = { ...this.state };
        const { chargeTypes } = { ...contractTemplate };
        const typesToOmit = (chargeTypes ?? []).map((c) => c.chargeTypeId) ?? [];
        // pass in types already selected
        this.chargeTypeSelectorRef.current.open(null, [...typesToOmit], contractTemplate.isProjectContract);
    };

    onAddChargeTypeGroupsCallback = (selections) => {
        const { contractTemplate } = { ...this.state };
        let updated = [...contractTemplate.chargeTypeGroups] ?? [];

        // get selections from the popup
        const selectedChargeTypeGroups = [...selections].map((x) => ({
            contractTemplateId: contractTemplate.id || 0,
            chargeTypeGroupId: x.value,
            invoiceDescription: x.label,
            chargeTypes: x.chargeTypes,
            chargeTypeBillingRates: [],
            billingRates: [],
        }));

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypeGroups]);

        contractTemplate.chargeTypeGroups = [...updated];

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onAddChargeTypesCallback = (selections) => {
        const { contractTemplate } = { ...this.state };
        let updated = [...contractTemplate.chargeTypes] ?? [];

        // get selections from the popup
        const selectedChargeTypes = [...selections].map((x) => ({
            order: null, /* append to the end, user can then order them */
            contractTemplateId: contractTemplate.id || 0,
            chargeTypeId: x.id,
            chargeTypeEligibilityId: null,
            description: x.description,
            chargeUnits: x.unitsName,
            group: x.appliesToGroup,
            employees: x.appliesToUsers,
            equipment: x.appliesToEquipment,
            billable: x.isBillable,
            resourceTypeId: x.resourceTypeId,
        }));

        // merge additions with current charges
        updated = updated.concat([...selectedChargeTypes]);

        // reorder everything after the add
        updated = updated.map((x, index) => ({ ...x, ...{ order: index += 1 } }));

        contractTemplate.chargeTypes = [...updated];

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onAllowNonFlaggingChanged = (value) => {
        const { contractTemplate } = { ...this.state };
        contractTemplate.enableNonFlaggingTimesheets = value;
        // FOR NOW, PER CW:
        // Hide the allow self-dispatch button (since self-dispatch isn't yet finished)
        // and default to true when the non-flag contract is set up.
        contractTemplate.allowSelfDispatch = !!value;
        this.setState({ contractTemplate });
    };

    onRemoveCTGroup = (chargeTypeGroupId) => {
        const { contractTemplate } = { ...this.state };

        contractTemplate.chargeTypeGroups = contractTemplate.chargeTypeGroups
            .filter((x) => x.chargeTypeGroupId !== chargeTypeGroupId);

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onAutoCalculateDriveTimeBillingChanged = (value) => {
        const { contractTemplate } = { ...this.state };
        contractTemplate.autoCalculateDriveTimeBilling = value;
        this.setState({ contractTemplate });
    };

    onAllowSelfDispatchChanged = (value) => {
        const { contractTemplate } = { ...this.state };
        contractTemplate.allowSelfDispatch = value;
        // When disabling self-dispatch, non-flag should also be disabled.
        if (!value) {
            contractTemplate.enableNonFlaggingTimesheets = false;
        }
        this.setState({ contractTemplate });
    };

    onRemoveChargeType = (chargeTypeIndex) => {
        const { contractTemplate } = { ...this.state };
        const chargeTypes = [...contractTemplate.chargeTypes];
        chargeTypes.splice(chargeTypeIndex, 1);
        contractTemplate.chargeTypes = [...chargeTypes];
        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onReorderChargeTypes = () => {
        // clone the list of charge types, to avoid mutating state.
        // pass to the widget.
        this.setState((state) => {
            const { contractTemplate } = { ...state };
            const selectedSortableChargeTypes = contractTemplate.chargeTypes;
            return { showReorderWidget: true, selectedSortableChargeTypes };
        });
    };

    onChange = onFieldChange;
    onSelectChanged = onReactSelectChanged;
    onYesNoChanged = onYesNoChanged;

    onSubmit = async () => {
        const { contractTemplate, isNew, saving } = { ...this.state };
        if ((contractTemplate.chargeTypes ?? []).length <= 0) {
            toasty.error('Invalid Contract Template', 'Please add charge types.');
            return false;
        }
        if (!saving) {
            this.setState({ saving: true });

            if (isNew) {
                await this.create({ ...contractTemplate });
            } else {
                await this.update({ ...contractTemplate });
            }
        }
    };

    create = async (template) => {
        const result = await httpClient.andGetResponse(
            httpClient.types.post,
            ApiRoutes.contractTemplate.create(),
            { ...template },
            'Error Creating Template',
        );

        if (result) {
            toasty.success('Template saved.');
            this.context.history.push(AppNavPaths.ContractTemplateIndex);
        } else {
            this.setState({ saving: false });
        }
    };

    // #endregion

    // #region SUBMISSION
    handleSaveError = handleFormSaveError;

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const contract_template_id = this.props.match.params.id;

            const [
                contractUnionWorkTypes,
                contractScheduleTypes,
                billingCycles,
                operationalCategories,
                employeeGroups,
                certifications,
                jobTypes,
            ] = await Promise.all([
                httpClient
                    .js(ApiRoutes.typeAheads.contractUnionWorkTypes())
                    .catch(() => console.log('Unable to get contract work types.')),
                httpClient
                    .js(ApiRoutes.typeAheads.contractScheduleTypes())
                    .catch(() => console.log('Unable to get contract schedule types.')),
                httpClient
                    .js(ApiRoutes.typeAheads.billingCycles())
                    .catch(() => console.log('Unable to get billing cycles.')),
                httpClient.js(ApiRoutes.typeAheads.operationalCategories())
                    .catch(() => console.log('Unable to get operational categories.')),
                httpClient.js(ApiRoutes.typeAheads.employeeGroups()),
                httpClient.js(ApiRoutes.certification.all())
                    .catch(() => console.log('Unable to get certifications.')),
                httpClient.js(ApiRoutes.typeAheads.jobTypes()),
            ]);

            let contractTemplate = null;
            if (contract_template_id) {
                contractTemplate = await httpClient.js(ApiRoutes.contractTemplate.byId(contract_template_id));
            } else {
                contractTemplate = new ContractTemplate();
            }

            if (!contractTemplate) {
                this.context.history.push(AppNavPaths.NotFound);
                return false;
            }

            const isNew = parseInt(contractTemplate.id ?? 0) <= 0;

            this.setState({
                loading: false,
                isNew,
                contractTemplate: { ...contractTemplate },
                originalData: JSON.stringify({ ...contractTemplate }),
                contractUnionWorkTypes,
                contractScheduleTypes,
                billingCycles,
                operationalCategories,
                employeeGroups,
                certifications,
                jobTypes,
            });
        }
    }

    // #endregion

    // -- Certifications -------------------------------------------------------
    toggleCertificationModal = () => {
        const { contractTemplate } = this.state;

        Modal.show({
            title: 'Certifications',
            onSubmit: (values) => this.onAddCertifications(values),
            SaveButtonProps: {
                label: 'Add',
            },
            body: () => (
                <ValidatedSelect
                    id="selectedCertifications"
                    name="selectedCertifications"
                    label="Certification(s)"
                    isMulti
                    options={this.state.certifications
                        // omit ones already on the contract
                        .filter((c) => !(contractTemplate.contractTemplateCertifications ?? []).some((k) => k.certificationId === c.id))}
                    getOptionLabel={(o) => o.name ?? o.label}
                    getOptionValue={(o) => o.id}
                    valueField="id"
                    required
                    error="You must select a certification"
                />
            ),
        });
    };

    onAddCertifications = ({ selectedCertifications }) => {
        const { contractTemplate } = this.state;

        const already = new Set((contractTemplate.contractTemplateCertifications ?? []).map((c) => c.certificationId));

        const additions = selectedCertifications
            .filter((c) => !already.has(c.id))
            .map((c) => ({
                certificationId: c.id,
                certificationName: c.name ?? c.label,
                abbreviation: c.abbreviation,
            }));

        contractTemplate.contractTemplateCertifications = [
            ...(contractTemplate.contractTemplateCertifications ?? []),
            ...additions,
        ];

        this.setState({
            contractTemplate,
        });
    };

    onRemoveCertification = (certificationId) => {
        const { contractTemplate } = this.state;

        contractTemplate.contractTemplateCertifications = contractTemplate.contractTemplateCertifications
            .filter((c) => c.certificationId !== certificationId);

        this.setState({ contractTemplate });
    };

    onContractJobTypeChanged = (value) => {
        const { contractTemplate } = { ...this.state };
        contractTemplate.contractTemplateJobTypes = value.map((v) => ({ id: uuid(), jobTypeId: v.value }));

        this.setState({ contractTemplate });
    };

    // #region BASE FORM EVENTS
    resetForm = () => this.setState({ formValidated: false, saving: false });

    update = async (template) => {
        const result = await httpClient.andGetResponse(
            httpClient.types.put,
            ApiRoutes.contractTemplate.byId(template.id),
            { ...template },
            'Error Saving Template',
        );
        if (result) {
            toasty.success('Template saved.');
            this.context.history.go(0);
        } else {
            this.setState({ saving: false });
        }
    };

    updateChargeTypeEligibility = (index, eligibilityId) => {
        const { contractTemplate } = { ...this.state };
        const chargeType = { ...contractTemplate.chargeTypes[index] };
        chargeType.chargeTypeEligibilityId = eligibilityId;
        const chargeTypes = [...contractTemplate.chargeTypes];
        chargeTypes[index] = { ...chargeType };
        contractTemplate.chargeTypes = [...chargeTypes];
        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    renderChargeTypesRows = () => {
        const { contractTemplate } = { ...this.state };
        const chargeTypes = [...contractTemplate.chargeTypes];

        if (!chargeTypes) return '';

        return chargeTypes.map((chargeType, chargeTypeIndex) => {
            const eligibilityValid = !!this.state.formValidated && !chargeType.chargeTypeEligibilityId;
            return (
                <tr key={chargeTypeIndex}>
                    <td className="text-center">{chargeType.order ?? ''}</td>
                    <td>
                        {chargeType.description}
                    </td>
                    <td className="text-center">{chargeType.chargeUnits}</td>
                    <td>{chargeType.group}</td>
                    <td>{chargeType.employees}</td>
                    <td>{chargeType.equipment}</td>
                    <td className="text-center">
                        <YesNoBadge value={chargeType.billable} />
                    </td>
                    <td className={cls({ 'p-0': eligibilityValid })}>
                        <div className={cls('h-100 w-100', { 'p-2 border border-danger': eligibilityValid })}>
                            <ButtonGroup>
                                {
                                    ChargeTypeEligibilities.map((eligibility, eligibilityIndex) => (
                                        <Button
                                            key={eligibilityIndex}
                                            type="button"
                                            color="secondary"
                                            size="sm"
                                            className={cls('pt-0 pb-0 pl-1 pr-1 mr-1', { active: eligibility.id === chargeType.chargeTypeEligibilityId })}
                                            onClick={() => {
                                                this.updateChargeTypeEligibility(chargeTypeIndex, eligibility.id);
                                            }}
                                        >
                                            {eligibility.name}
                                        </Button>
                                    ))
                                }
                            </ButtonGroup>
                            <Input
                                className="hidden-validation"
                                required
                                value={chargeType.chargeTypeEligibilityId ?? ''}
                                error="Eligibility selection is required."
                            />
                        </div>
                    </td>
                    <td className="text-center">
                        <Button
                            color="link"
                            title={`Remove ${chargeType.description}`}
                            className="text-danger"
                            onClick={() => this.onRemoveChargeType(chargeTypeIndex)}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </Button>
                    </td>
                </tr>
            );
        });
    };

    renderChargeTypeGroupsRows = () => {
        const { contractTemplate } = { ...this.state };
        const { chargeTypeGroups } = contractTemplate;

        if (!chargeTypeGroups) {
            return '';
        };

        return chargeTypeGroups.map((chargeTypeGroup) => (
            <tr key={chargeTypeGroup.chargeTypeGroupId}>
                <td>
                    {chargeTypeGroup.invoiceDescription}
                </td>
                <td>{chargeTypeGroup?.chargeTypes ?? ''}</td>
                <td className="text-center">
                    <IconButton
                        title={`Remove ${chargeTypeGroup.description}`}
                        onClick={() => this.onRemoveCTGroup(
                            chargeTypeGroup.chargeTypeGroupId,
                        )}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="text-danger cursor-pointer"
                        />
                    </IconButton>
                </td>
            </tr>
        ));
    };

    // #endregion

    onAddPayScheduleCallback = (selections) => {
        const { contractTemplate } = { ...this.state };
        let updated = [...contractTemplate.contractTemplatePaySchedules] ?? [];

        // get selections from the popup
        const selectedPaySchedules = [...selections].map((x) => ({
            id: uuid(),
            contractTemplateId: contractTemplate.id || 0,
            payScheduleId: x.id,
            paySchedule: {
                id: x.id,
                governingBody: x.governingBody,
                state: x.state,
                counties: x.counties,
                reg: x.reg,
                ot: x.ot,
                fringe: x.fringe,
            },
        }));

        const contractHasStateOnlyPS = contractTemplate.contractTemplatePaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const selectionsStateOnlyPS = selectedPaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );
        const contractHasItems = contractTemplate.contractTemplatePaySchedules != null
            && contractTemplate.contractTemplatePaySchedules.length > 0;

        // Contract has a state only pay schedule, nothing else can be added
        if (contractHasStateOnlyPS?.length > 0) {
            toasty.error(
                'Pay Schedule Conflict',
                'There is a pay schedule for the entire state. No more pay schedules can be added.',
            );
            return;
        }

        // Selections contain a state only pay schedule, but contract already has a pay schedule
        if (selectionsStateOnlyPS.length > 0 && contractHasItems) {
            toasty.error(
                'Pay Schedule Conflict',
                'Unable to add pay schedule for the entire state due to an existing pay schedule.',
            );
            return;
        }

        // Selections contain a state only pay schedule, with multiple selections
        if (
            selectionsStateOnlyPS.length > 0
            && selectedPaySchedules.length > 1
        ) {
            toasty.error(
                'Pay Schedule Conflict',
                'Cannot add other pay schedules with a pay schedule for the entire state.',
            );
            return;
        }

        // merge additions with current charges
        updated = updated.concat([...selectedPaySchedules]);
        contractTemplate.contractTemplatePaySchedules = [...updated];

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onAddPaySchedules = () => {
        const { contractTemplate } = { ...this.state };
        const { contractTemplatePaySchedules } = contractTemplate;

        const paySchedulesNoCounties = contractTemplate.contractTemplatePaySchedules.filter(
            (x) => x.paySchedule.counties == null
                || x.paySchedule.counties.length == 0,
        );

        if (paySchedulesNoCounties?.length > 0) {
            toasty.error(
                'Pay Schedule Conflict',
                'There is a pay schedule for the entire state. No more pay schedules can be added.',
            );
            return;
        }

        const itemsToOmit = (contractTemplatePaySchedules ?? []).map((c) => c.payScheduleId) ?? [];

        this.payScheduleSelectorRef.current.open(null, [...itemsToOmit]);
    };

    handleIncentivesChange = (updatedIncentives) => {
        const { contractTemplate } = this.state;
        contractTemplate.payrollIncentives = updatedIncentives;

        this.setState({ contractTemplate });
    };

    onRemovePaySchedule = (payScheduleId) => {
        const { contractTemplate } = { ...this.state };

        contractTemplate.contractTemplatePaySchedules = contractTemplate.contractTemplatePaySchedules
            .filter((schedule) => schedule.paySchedule.id !== payScheduleId);

        this.setState({ contractTemplate: { ...contractTemplate } });
    };


    renderContractPaySchedules = () => {
        const { contractTemplate } = { ...this.state };
        const { contractTemplatePaySchedules } = contractTemplate;

        if (!contractTemplatePaySchedules) {
            return '';
        }

        return contractTemplatePaySchedules.map(
            (contractPaySchedule) => (
                <tr key={contractPaySchedule.paySchedule.id}>
                    <td>{contractPaySchedule.paySchedule.governingBody}</td>
                    <td>{contractPaySchedule.paySchedule.state}</td>
                    <td>
                        {contractPaySchedule.paySchedule.counties == null
                            || contractPaySchedule.paySchedule.counties.length == 0
                            ? 'All'
                            : contractPaySchedule.paySchedule.counties}
                    </td>
                    <td>{contractPaySchedule.paySchedule.reg}</td>
                    <td>{contractPaySchedule.paySchedule.ot}</td>
                    <td>{contractPaySchedule.paySchedule.fringe}</td>
                    <td className="text-center">
                        <IconButton
                            title={`Remove ${contractPaySchedule.governingBody}`}
                            onClick={() => this.onRemovePaySchedule(
                                contractPaySchedule.paySchedule.id,
                            )}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="text-danger cursor-pointer"
                            />
                        </IconButton>
                    </td>
                </tr>
            ),
        );
    };

    renderContractUSCountyGroups = () => {
        const { contractTemplate } = { ...this.state };
        const { contractTemplateUSCountyGroups } = contractTemplate;

        if (!contractTemplateUSCountyGroups) {
            return '';
        }

        return contractTemplateUSCountyGroups.map(
            (contractUSCountyGroup) => (
                <tr key={contractUSCountyGroup.usCountyGroup?.id}>
                    <td>{contractUSCountyGroup.usCountyGroup?.description}</td>
                    <td>{contractUSCountyGroup.usCountyGroup?.state}</td>
                    <td>
                        {contractUSCountyGroup.usCountyGroup?.counties == null
                            || contractUSCountyGroup.usCountyGroup.counties.length == 0
                            ? 'All'
                            : contractUSCountyGroup.usCountyGroup.counties}
                    </td>
                    <td className="text-center">
                        <IconButton
                            title={`Remove ${contractUSCountyGroup.usCountyGroup?.description}`}
                            onClick={() => this.onRemoveUSCountyGroup(
                                contractUSCountyGroup.usCountyGroupId,
                            )}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="text-danger cursor-pointer"
                            />
                        </IconButton>
                    </td>
                </tr>
            ),
        );
    };

    onRemoveUSCountyGroup = (usCountyGroupId) => {
        const { contractTemplate } = { ...this.state };

        contractTemplate.contractTemplateUSCountyGroups = contractTemplate.contractTemplateUSCountyGroups
            .filter((countyGroup) => countyGroup.usCountyGroupId !== usCountyGroupId);

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    onAddUSCountyGroups = () => {
        const { contractTemplate } = { ...this.state };
        const { contractTemplateUSCountyGroups } = contractTemplate;

        const itemsToOmit = (contractTemplateUSCountyGroups ?? []).map((c) => c.usCountyGroupId) ?? [];

        this.uSCountyGroupSelectorRef.current.open(null, [...itemsToOmit]);
    };

    onAddUSCountyGroupCallback = (selections) => {
        const { contractTemplate } = { ...this.state };
        let updated = [...(contractTemplate.contractTemplateUSCountyGroups ?? [])];

        const selectedUSCountyGroups = [...selections].map((x) => ({
            id: null,
            contractTemplateId: contractTemplate.id,
            usCountyGroupId: x.id,
            usCountyGroup: {
                id: x.id,
                description: x.description,
                state: x.state,
                counties: x.counties,
            },
        }));

        updated = updated.concat([...selectedUSCountyGroups]);
        contractTemplate.contractTemplateUSCountyGroups = [...updated];

        this.setState({ contractTemplate: { ...contractTemplate } });
    };

    render() {
        const
            {
                contractTemplate,
                errors,
                formValidated,
                isNew,
                loading,
                originalData,
                saving,
                contractUnionWorkTypes,
                contractScheduleTypes,
                billingCycles,
                operationalCategories,
                jobTypes,
            } = { ...this.state };

        if (loading) {
            return (
                <div className="h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }

        const { formIsOpen, tenant } = this.context;
        const { tenantSettings } = tenant;

        const formTitle = isNew ? 'New Contract Template' : 'Edit Contract Template';

        const selfDispatchTimesheetsEnabled = !!tenantSettings.enableSelfDispatchTimesheets;

        const selectedWorkType = (
            contractUnionWorkTypes ?? []
        ).find(
            (x) => contractTemplate.contractUnionWorkTypeId
                === x.value,
        ) ?? '';

        const allowRentalChargeTypes = !!tenantSettings.allowRentalChargeTypes;
        const hasRentalChargeTypes = (contractTemplate.chargeTypes ?? []).some(
            (ct) => ct.resourceTypeId === ChargeTypeResourceTypes.Rental,
        ) || (contractTemplate.chargeTypeGroups ?? []).some((g) => g.hasRentalChargeTypes);
        const isRentalToggleLocked = hasRentalChargeTypes && contractTemplate.isProjectContract;
        const isInvoicingAllowed = !!tenantSettings.invoicingEnabled;
        const isChargeTypeGroupsAllowed = !!tenantSettings.chargeTypeGroupsEnabled && !contractTemplate.enableNonFlaggingTimesheets;
        const isCertificationsAllowed = !!tenantSettings.enableCertification && contractTemplate?.certificationRequired;
        const isUnionWorkEnforced = !!tenantSettings.enforceContractUnionWork;
        const workCardOptions = getWorkCardOptions(tenantSettings);
        const doubleTimeEnabled = !!tenantSettings.enableDoubleTimePayroll;

        const glStringsEnabled = !!tenantSettings.glStringsEnabled;
        const enableAutoEscalatingContractRates = !!tenantSettings.enableAutoEscalatingContractRates;
        const enablePrevailingWageContracts = !!tenantSettings.enablePrevailingWageContracts;
        const contractsCanEnforceProjectManagers = !!tenantSettings.contractsCanEnforceProjectManagers;
        const showWorkCardOnTimesheet = !!tenantSettings.showWorkCardOnTimesheet;

        const timesheetCheckinsEnabled = !!tenantSettings.enableContractTimesheetCheckins;
        const enableContractDateRange = !!tenantSettings.enableContractDateRange;
        const isBillingPerJobTypeAllowed = !!tenantSettings.enableContractJobTypeBillingSetting;
        const combineCharges = Boolean(tenantSettings.combineRentalNonRentalChargesOnRentalInvoices);

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, JSON.stringify({ ...contractTemplate }))}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="contractTemplateForm"
                    formHeadingIcon={faFileContract}
                    formHeading={formTitle}
                    formName="contractTemplateForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={AppNavPaths.ContractTemplateIndex}
                    backLabel="Back to Contract Templates"
                >
                    <FormBlocker show={!!formIsOpen} />

                    {Boolean(contractTemplate?.id) && (
                        <>
                            <SubHeading>Template Status</SubHeading>

                            <YesNoButtons
                                label="Set Status"
                                name="contractTemplate.active"
                                value={contractTemplate.active}
                                onClick={this.onYesNoChanged}
                                options={[
                                    { value: true, label: 'Active' },
                                    { value: false, label: 'Inactive' },
                                ]}
                            />

                            <hr />
                        </>
                    )}

                    <Input
                        label="Description"
                        id="description"
                        name="contractTemplate.description"
                        value={contractTemplate.description ?? ''}
                        onChange={this.onChange}
                        placeholder="Enter Description (max 250 characters)"
                        maxLength="250"
                        required
                        error="Description is required."
                    />

                    {
                        !contractTemplate.isProjectContract
                        && !!selfDispatchTimesheetsEnabled
                        && (
                            <YesNoButtons
                                label="Is this a Non-Flagging template?"
                                name="contractTemplate.enableNonFlaggingTimesheets"
                                onClick={(value) => this.onAllowNonFlaggingChanged(value)}
                                value={contractTemplate.enableNonFlaggingTimesheets}
                            />
                        )
                    }

                    {!contractTemplate.enableNonFlaggingTimesheets && (
                        <>
                            {Boolean(allowRentalChargeTypes) && (
                                <>
                                    <YesNoButtons
                                        id="isProjectContract"
                                        label="Is this a Rental template?"
                                        name="contractTemplate.isProjectContract"
                                        onClick={this.onYesNoChanged}
                                        value={contractTemplate.isProjectContract}
                                        disabled={isRentalToggleLocked}
                                    />
                                    {isRentalToggleLocked && (
                                        <UncontrolledTooltip target="isProjectContract">
                                            This option is locked because this template has rental charge types.
                                        </UncontrolledTooltip>
                                    )}

                                    <Collapse isOpen={contractTemplate.isProjectContract || combineCharges}>
                                        <ValidatedSelect
                                            id="billingCycle"
                                            name="contractTemplate.billingCycleId"
                                            label="Billing Cycle"
                                            required={!!contractTemplate.isProjectContract || combineCharges}
                                            options={billingCycles}
                                            value={
                                                (
                                                    billingCycles ?? []
                                                ).find(
                                                    (x) => contractTemplate.billingCycleId
                                                        === x.value,
                                                ) ?? ''
                                            }
                                            onChange={
                                                this.onSelectChanged
                                            }
                                            error="Billing Cycle selection is required."
                                        />

                                        {contractTemplate.billingCycleId == BillingCycles.Weekly ? (
                                            <ValidatedSelect
                                                id="weeklyBillingCycleEndDay"
                                                name="contractTemplate.weeklyBillingCycleEndDay"
                                                label="Billing Cycle End Date"
                                                options={DayOfWeekSelect}
                                                value={
                                                    (
                                                        DayOfWeekSelect ?? []
                                                    ).find(
                                                        (x) => contractTemplate.weeklyBillingCycleEndDay
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />
                                        ) : contractTemplate.billingCycleId == BillingCycles.Monthly && (
                                            <ValidatedSelect
                                                id="monthlyBillingCycleEndDay"
                                                name="contractTemplate.monthlyBillingCycleEndDay"
                                                label="Billing Cycle End Date"
                                                options={DayOfMonthSelect}
                                                value={
                                                    (
                                                        DayOfMonthSelect ?? []
                                                    ).find(
                                                        (x) => contractTemplate.monthlyBillingCycleEndDay
                                                            === x.value,
                                                    ) ?? ''
                                                }
                                                onChange={
                                                    this.onSelectChanged
                                                }
                                            />
                                        )}
                                    </Collapse>

                                    {Boolean(contractTemplate.isProjectContract) && (
                                        <YesNoButtons
                                            label="This template supports quick rentals"
                                            name="contractTemplate.isQuickRentalContract"
                                            onClick={this.onYesNoChanged}
                                            value={contractTemplate.isQuickRentalContract}
                                        />
                                    )}
                                </>
                            )}

                            {enableContractDateRange && (
                                <ValidatedSelect
                                    id="warningThresholdInDays"
                                    name="contractTemplate.warningThresholdInDays"
                                    label="Warning Threshold (Days)"
                                    options={WarningThresholdOptions}
                                    required
                                    value={
                                        (
                                            WarningThresholdOptions
                                            ?? []
                                        ).find(
                                            (x) => contractTemplate.warningThresholdInDays === x.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    error="Warning Threshold is required."
                                    hideClear
                                />
                            )}

                            <NumericInput
                                id="estimatedShiftLengthInHours"
                                label="Estimated Shift Length"
                                name="contractTemplate.estimatedShiftLengthInHours"
                                formGroupClassName="w-50"
                                value={contractTemplate.estimatedShiftLengthInHours ?? ''}
                                onChange={this.onChange}
                                min={0}
                                step={0.25}
                                preventNegative
                                endAddon={<span className="text-muted">hrs</span>}
                                TipComponentProps={{
                                    label: 'Dispatch will be notified any time a job goes over this duration.',
                                }}
                            />

                            <ValidatedSelect
                                id="workOrderTypeId"
                                name="contractTemplate.workOrderTypeId"
                                label="Require Work Order Number?"
                                options={WorkOrderNumberOptions}
                                required
                                value={WorkOrderNumberOptions.find((wt) => contractTemplate.workOrderTypeId === wt.value) ?? ''}
                                onChange={this.onSelectChanged}
                                error="A work order type selection is required."
                            />

                            {
                                (!!contractTemplate.workOrderTypeId && contractTemplate.workOrderTypeId !== ChargeTypeEligbility.DoNotUse)
                                && (
                                    <YesNoButtons
                                        label="Linked Work Orders?"
                                        name="contractTemplate.allowLinkedWorkOrders"
                                        value={contractTemplate.allowLinkedWorkOrders}
                                        onClick={this.onYesNoChanged}
                                    />
                                )
                            }

                            {isInvoicingAllowed && (
                                <>
                                    {glStringsEnabled && (
                                        <YesNoButtons
                                            id="requireGLString"
                                            label="Require GL String"
                                            name="contractTemplate.requireGLString"
                                            onClick={this.onYesNoChanged}
                                            value={contractTemplate.requireGLString}
                                        />
                                    )}

                                    {contractsCanEnforceProjectManagers && (
                                        <YesNoButtons
                                            id="requireProjectInformation"
                                            label="Require Project Information when building Jobs?"
                                            name="contractTemplate.requireProjectInformation"
                                            onClick={this.onYesNoChanged}
                                            value={contractTemplate.requireProjectInformation}
                                        />
                                    )}
                                </>
                            )}

                            {(Boolean(contractTemplate.isProjectContract) || tenantSettings.useContractProjects === 2) && (
                                <YesNoButtons
                                    id="requiresProjectForQuickSale"
                                    label="Quick Sale Requires Project"
                                    name="contractTemplate.requiresProjectForQuickSale"
                                    onClick={this.onYesNoChanged}
                                    value={contractTemplate.requiresProjectForQuickSale}
                                    TipComponentProps={{
                                        label: 'When enabled, all Quick Sales created under this contract must be linked to a specific project.',
                                    }}
                                />
                            )}

                            {enableAutoEscalatingContractRates && (
                                <YesNoButtons
                                    label="Auto-Escalating Rates"
                                    name="contractTemplate.autoEscalatingRates"
                                    value={contractTemplate.autoEscalatingRates}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            {enablePrevailingWageContracts && (
                                <>
                                    <YesNoButtons
                                        label="Prevailing Wage"
                                        name="contractTemplate.prevailingWage"
                                        value={contractTemplate.prevailingWage}
                                        onClick={this.onYesNoChanged}
                                    />

                                    <Collapse isOpen={!contractTemplate.prevailingWage}>
                                        <YesNoButtons
                                            label="Require a pay schedule for this contract?"
                                            name="contractTemplate.requirePaySchedule"
                                            value={contractTemplate.requirePaySchedule}
                                            onClick={this.onYesNoChanged}
                                        />
                                    </Collapse>
                                </>
                            )}

                            {Boolean(tenantSettings.enableCertification) && (
                                <YesNoButtons
                                    label="Jobs may require certifications"
                                    name="contractTemplate.certificationRequired"
                                    value={contractTemplate.certificationRequired}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            <YesNoButtons
                                label="Setup Charges Required"
                                name="contractTemplate.setupChargesRequired"
                                value={contractTemplate.setupChargesRequired}
                                onClick={this.onYesNoChanged}
                            />

                            <YesNoButtons
                                label="Permit Fees Apply"
                                name="contractTemplate.permitFeesApply"
                                value={contractTemplate.permitFeesApply}
                                onClick={this.onYesNoChanged}
                            />

                            {timesheetCheckinsEnabled && (
                                <YesNoButtons
                                    label="Require Employee Check-ins"
                                    name="contractTemplate.requireEmployeeCheckins"
                                    value={contractTemplate.requireEmployeeCheckins}
                                    onClick={this.onYesNoChanged}
                                />
                            )}

                            <YesNoButtons
                                label="Auto-Calculate Drive Time for Billing"
                                name="autoCalculateDriveTimeBilling"
                                value={contractTemplate.autoCalculateDriveTimeBilling}
                                onClick={(value) => this.onAutoCalculateDriveTimeBillingChanged(value)}
                            />

                            <YesNoButtons
                                label="Requires Job Approval"
                                name="contractTemplate.requiresJobApproval"
                                value={contractTemplate.requiresJobApproval}
                                onClick={this.onYesNoChanged}
                            />

                            <YesNoButtons
                                id="requireProjectCompletionReview"
                                label="Require crew lead to report project completion?"
                                name="contractTemplate.requireProjectCompletionReview"
                                value={contractTemplate.requireProjectCompletionReview}
                                onClick={this.onYesNoChanged}
                                TipComponentProps={{
                                    label: 'When enabled, the crew lead is asked "Was the project completed on this timesheet?" during their timesheet review.',
                                }}
                            />

                            {showWorkCardOnTimesheet && (
                                <ValidatedSelect
                                    id="showWorkCardOnTimesheet"
                                    name="contractTemplate.workCardTypeId"
                                    label="Work Card Required?"
                                    options={workCardOptions}
                                    required
                                    value={
                                        workCardOptions.find(
                                            (wc) => contractTemplate.workCardTypeId === wc.value,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    error="A work card type selection is required."
                                />
                            )}

                            {(this.context.checkUserHasPermission(
                                ApplicationPermissions.dispatch_plus_view,
                            ) || this.context.user?.isAdmin) && this.state.operationalCategories?.length > 0 &&
                                (
                                    <ValidatedSelect
                                        id="operationalCategoryId"
                                        name="contractTemplate.operationalCategoryId"
                                        label="Operational Category"
                                        options={operationalCategories}
                                        isClearable
                                        value={
                                            operationalCategories.find(
                                                (oc) => contractTemplate.operationalCategoryId === oc.value,
                                            ) ?? null
                                        }
                                        onChange={this.onSelectChanged}
                                    />
                                )}

                            {isUnionWorkEnforced && !contractTemplate.enableNonFlaggingTimesheets ? (
                                <>
                                    <SubHeading>Union Settings</SubHeading>
                                    <div className="mt-4">
                                        <ValidatedSelect
                                            id="contractUnionWorkTypeId"
                                            name="contractTemplate.contractUnionWorkTypeId"
                                            label="Union Work Type"
                                            options={contractUnionWorkTypes}
                                            required
                                            error="Please select a work type for this contract"
                                            value={selectedWorkType}
                                            onChange={
                                                this.onSelectChanged
                                            }
                                        />

                                        <ValidatedSelect
                                            id="contractScheduleTypeId"
                                            name="contractTemplate.contractScheduleTypeId"
                                            label="Pay Schedule"
                                            options={contractScheduleTypes}
                                            required={selectedWorkType?.isUnionWork === true}
                                            error="A pay schedule selection is required"
                                            value={(
                                                contractScheduleTypes ?? []
                                            ).find(
                                                (x) => contractTemplate.contractScheduleTypeId === x.value,
                                            ) ?? ''}
                                            onChange={
                                                this.onSelectChanged
                                            }
                                        />

                                        <Collapse isOpen={selectedWorkType?.isUnionWork === true}>
                                            {Boolean(doubleTimeEnabled) && (
                                                <YesNoButtons
                                                    label="Sunday Has Double Time Pay?"
                                                    name="contractTemplate.hasSundayDT"
                                                    required
                                                    value={contractTemplate.hasSundayDT}
                                                    onClick={this.onYesNoChanged}
                                                />
                                            )}

                                            <YesNoButtons
                                                label="Does this contract use a different billing schedule than above?"
                                                required
                                                name="contractTemplate.billingHasSeparateContractSchedule"
                                                value={contractTemplate.billingHasSeparateContractSchedule}
                                                onClick={this.onYesNoChanged}
                                            />

                                            <Collapse isOpen={Boolean(contractTemplate.billingHasSeparateContractSchedule)}>
                                                <ValidatedSelect
                                                    id="billingContractScheduleTypeId"
                                                    name="contractTemplate.billingContractScheduleTypeId"
                                                    label="Billing Schedule"
                                                    options={contractScheduleTypes}
                                                    required={Boolean(contractTemplate.billingHasSeparateContractSchedule)}
                                                    error="A billing schedule selection is required"
                                                    value={(
                                                        contractScheduleTypes ?? []
                                                    ).find(
                                                        (x) => contractTemplate.billingContractScheduleTypeId === x.value,
                                                    ) ?? ''}
                                                    onChange={
                                                        this.onSelectChanged
                                                    }
                                                />

                                                <Collapse
                                                    isOpen={(
                                                        contractScheduleTypes ?? []
                                                    ).find(
                                                        (x) => contractTemplate.billingContractScheduleTypeId === x.value,
                                                    )?.days === 0}
                                                >
                                                    <YesNoButtons
                                                        label="Disable OT/DT Billing for Sunday?"
                                                        name="contractTemplate.hasSundayRegularBilling"
                                                        value={contractTemplate.hasSundayRegularBilling}
                                                        onClick={this.onYesNoChanged}
                                                        required
                                                    />
                                                </Collapse>
                                            </Collapse>

                                            <YesNoButtons
                                                label="Does this contract use billing bump rules other than 2-4-8?"
                                                required
                                                name="contractTemplate.billingHasCustomBumpRules"
                                                value={contractTemplate.billingHasCustomBumpRules}
                                                onClick={this.onYesNoChanged}
                                            />

                                            <Collapse isOpen={Boolean(contractTemplate.billingHasCustomBumpRules)}>
                                                <TipAlert
                                                    label="Leaving this section empty will disable billing bump rules for this contractTemplate."
                                                    containerClassName="mt-4"
                                                />

                                                <Card className="mt-2">
                                                    <ContractBumpRuleGrid
                                                        bumpRules={contractTemplate.contractTemplateBumpRules}
                                                        onBumpRulesChange={(updatedBumpRules) => {
                                                            contractTemplate.contractTemplateBumpRules = updatedBumpRules;

                                                            this.setState({ contractTemplate });
                                                        }}
                                                    />
                                                </Card>
                                            </Collapse>
                                        </Collapse>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {(Boolean(doubleTimeEnabled) || Boolean(contractTemplate.companyUsesContractSchedule)) && (
                                        <>
                                            <SubHeading>Billing/Payroll Settings</SubHeading>
                                            <div className="mt-4">
                                                {Boolean(doubleTimeEnabled) && (
                                                    <YesNoButtons
                                                        label="Sunday Has Double Time Pay?"
                                                        name="contractTemplate.hasSundayDT"
                                                        value={contractTemplate.hasSundayDT}
                                                        onClick={this.onYesNoChanged}
                                                    />
                                                )}

                                                {(Boolean(contractTemplate.companyUsesContractSchedule) || isUnionWorkEnforced) && (
                                                    <ValidatedSelect
                                                        id="contractScheduleTypeId"
                                                        name="contractTemplate.contractScheduleTypeId"
                                                        label={contractTemplate.companyUsesContractSchedule ? 'Billing Schedule' : 'Pay Schedule'}
                                                        options={contractScheduleTypes}
                                                        required={Boolean(contractTemplate.companyUsesContractSchedule)}
                                                        error="A billing schedule selection is required"
                                                        value={(
                                                            contractScheduleTypes ?? []
                                                        ).find(
                                                            (x) => contractTemplate.contractScheduleTypeId === x.value,
                                                        ) ?? ''}
                                                        onChange={
                                                            this.onSelectChanged
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {isBillingPerJobTypeAllowed && selectedWorkType?.isUnionWork !== true && (
                        <>
                            <SubHeading>Job Type Settings</SubHeading>
                            <div className="mt-4">
                                <ValidatedSelect
                                    id="contractTemplate.contractTemplateJobTypes"
                                    name="contractTemplate.contractTemplateJobTypes"
                                    label="Job Types with Custom Billing Rates"
                                    isMulti
                                    options={jobTypes}
                                    value={(jobTypes ?? []).filter((j) => (contractTemplate.contractTemplateJobTypes ?? [])
                                        .some(
                                            (x) => x.jobTypeId === j.value,
                                        ),
                                    )}
                                    onChange={this.onContractJobTypeChanged}
                                />

                                {selectedWorkType?.isUnionWork === true ? (
                                    <TipAlert
                                        tipText="Note"
                                        label="Custom billing rates per job type are disabled for Union Work contracts."
                                    />
                                ) : (
                                    <TipAlert
                                        label="Select the job types that require custom billing rates for this contract."
                                    />
                                )}
                            </div>
                        </>
                    )}

                    {!isUnionWorkEnforced && (contractTemplate.prevailingWage || contractTemplate.requirePaySchedule) && (
                        <>
                            {!tenantSettings.enablePrevailingWageCountyGroups ? (
                                <>
                                    <SubHeading>Pay Schedules</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        id="contractPrevailingWageTable"
                                        onAddFunction={this.onAddPaySchedules}
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening pay schedules form...
                                                </span>
                                            ) : (
                                                'Add Pay Schedule'
                                            )
                                        }
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={[
                                            {
                                                name: 'Description',
                                                class: 'ct-job-number',
                                            },
                                            {
                                                name: 'State',
                                                class: 'ct-job-name',
                                            },
                                            {
                                                name: 'Counties',
                                                class: 'ct-job-week',
                                            },
                                            {
                                                name: 'Reg',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: 'OT',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: 'Fringe',
                                                class: 'ct-job-active',
                                            },
                                            {
                                                name: '',
                                                class: 'ct-job-active',
                                            },
                                        ]}
                                        editable={false}
                                        entities={contractTemplate.contractTemplatePaySchedules}
                                        rowRenderer={
                                            this.renderContractPaySchedules
                                        }
                                        noDataText="No pay schedules found."
                                    />
                                </>
                            ) : (
                                <>
                                    <SubHeading>County Groups</SubHeading>
                                    <SimpleTable
                                        className="mb-0"
                                        id="contractPrevailingWageUSCountyGroupsTable"
                                        onAddFunction={this.onAddUSCountyGroups}
                                        addLabelText={
                                            this.context.formIsOpen ? (
                                                <span>
                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                    Opening county groups form...
                                                </span>
                                            ) : (
                                                'Add County Group'
                                            )
                                        }
                                        noTopBorder
                                        permission="contract"
                                        tableHeaderLabels={[
                                            {
                                                name: 'Description',
                                                class: 'ct-job-number',
                                            },
                                            {
                                                name: 'State',
                                                class: 'ct-job-name',
                                            },
                                            {
                                                name: 'Counties',
                                                class: 'ct-job-week',
                                            },
                                            {
                                                name: '',
                                                class: 'ct-job-active',
                                            },
                                        ]}
                                        editable={false}
                                        entities={contractTemplate.contractTemplateUSCountyGroups}
                                        rowRenderer={
                                            this.renderContractUSCountyGroups
                                        }
                                        noDataText="No county groups found."
                                    />
                                </>
                            )}
                            <FormGroup className="flex-row-reverse">
                                <FormCheckbox
                                    className="m-3"
                                    id="contractTemplate.prevailingWageAllowEmployeePayRate"
                                    name="contractTemplate.prevailingWageAllowEmployeePayRate"
                                    checked={!!contractTemplate.prevailingWageAllowEmployeePayRate}
                                    onChange={this.onChange}
                                    labelText="Use the employee pay rate if it exceeds the Pay Schedule rate"
                                />
                            </FormGroup>

                            {Boolean(contractTemplate.prevailingWage) && !tenantSettings.quirksEnabled && (
                                <Container>
                                    <SubHeading>Pay Incentives</SubHeading>
                                    <ContractPayrollIncentiveGrid
                                        incentives={contractTemplate.payrollIncentives}
                                        onIncentivesChange={this.handleIncentivesChange}
                                        userGroups={this.state.employeeGroups}
                                    />
                                </Container>
                            )}
                        </>
                    )}

                    {isCertificationsAllowed && (
                        <>
                            <SubHeading>Certifications</SubHeading>

                            <SimpleTable
                                id="certificationsTable"
                                className="mb-3"
                                entities={contractTemplate.contractTemplateCertifications}
                                noTopBorder
                                permission="contract"
                                addLabelText={this.context.formIsOpen ? (
                                    <span>
                                        <i className="fa fa-spin fa-circle-notch mr-1" />
                                        Opening certifications form
                                    </span>
                                ) : 'Add Certifications'}
                                onAddFunction={this.toggleCertificationModal}
                                editable={false}
                                tableHeaderLabels={[
                                    { name: 'Certification', class: 'text-left' },
                                    { name: 'Abbreviation', class: 'text-left' },
                                    { name: '', class: 'text-center' },
                                ]}
                                rowRenderer={() =>
                                    (contractTemplate.contractTemplateCertifications ?? []).map((c) => (
                                        <tr key={c.certificationId}>
                                            <td>{c.certificationName}</td>
                                            <td>{c.certificationAbbreviation || c.abbreviation}</td>
                                            <td className="text-center">
                                                <IconButton
                                                    size="sm"
                                                    onClick={() => this.onRemoveCertification(c.certificationId)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                    />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                noDataText="No certifications on this contract template."
                            />
                        </>
                    )}

                    <SubHeading>Charge Types</SubHeading>
                    <SimpleTable
                        className="mb-0"
                        onAddFunction={this.onAddChargeTypes}
                        addButtonEnabled
                        addLabelText={this.context.formIsOpen ? (
                            <span>
                                <i className="fa fa-spin fa-circle-notch mr-1" />
                                Opening charge types form...
                            </span>
                        ) : 'Add Charge Types'}
                        leftToolbarContent={(
                            <SmallButton
                                type="button"
                                disabled={!!this.context.formIsOpen || !(contractTemplate.chargeTypes ?? []).length}
                                onClick={this.onReorderChargeTypes}
                            >
                                <span className="fa fa-sort mr-2" />
                                Reorder
                            </SmallButton>
                        )}
                        noTopBorder
                        permission="contract_template"
                        tableHeaderLabels={[
                            { name: 'Order', class: 'text-center' },
                            { name: 'Description' },
                            { name: 'Charge Units', class: 'text-center' },
                            { name: 'Group' },
                            { name: 'Employees' },
                            { name: 'Equipment' },
                            { name: 'Billable', class: 'text-center' },
                            { name: 'Job Eligibility' },
                            { name: '', class: 'text-center' },
                        ]}
                        editable={false}
                        entities={[...contractTemplate.chargeTypes || []]}
                        rowRenderer={this.renderChargeTypesRows}
                        noDataText="No charge types found."
                    />

                    {!!(contractTemplate.chargeTypes ?? []).length && (
                        <FlexEndRow className="pt-1 pl-3 pr-4">
                            <Button
                                color="link"
                                size="sm"
                                className="site-link text-right"
                                onClick={() => {
                                    const { contractTemplate } = { ...this.state };
                                    const { chargeTypes } = { ...contractTemplate };
                                    const updated = [...chargeTypes.map((x) => ({ ...x, chargeTypeEligibilityId: _ContractChargeTypeEligibility.Required }))];
                                    contractTemplate.chargeTypes = updated;
                                    this.setState({ contractTemplate: { ...contractTemplate } });
                                }}
                            >
                                Require All Charge Types
                            </Button>
                        </FlexEndRow>
                    )}

                    {isChargeTypeGroupsAllowed && (
                        <>
                            <SubHeading>Charge Type Groups</SubHeading>
                            <SimpleTable
                                className="mb-3"
                                onAddFunction={this.onAddChargeTypeGroups}
                                addButtonEnabled
                                addLabelText={
                                    this.context.formIsOpen ? (
                                        <span>
                                            <i className="fa fa-spin fa-circle-notch mr-1" />
                                            Opening charge type group form...
                                        </span>
                                    ) : (
                                        'Add Charge Type Groups'
                                    )
                                }
                                refreshButtonEnabled={false}
                                noTopBorder
                                permission="contract"
                                tableHeaderLabels={[
                                    { name: 'Description' },
                                    { name: 'Charge Type(s)' },
                                    { name: '', class: 'text-center' },
                                ]}
                                editable={false}
                                entities={contractTemplate.chargeTypeGroups}
                                rowRenderer={this.renderChargeTypeGroupsRows}
                                noDataText="No charge type groups found."
                            />
                        </>
                    )}

                    <FlexCenterRow className="mt-4">
                        <Button
                            size="sm"
                            type="submit"
                            color="primary"
                            name="contractForm"
                        >
                            <FontAwesomeIcon
                                className="mr-2"
                                icon={faSave}
                            />
                            {!isNew ? 'Save Template' : 'Save New Contract Template'}
                        </Button>
                    </FlexCenterRow>
                </AppPageForm>

                <ChargeTypeSelect
                    ref={this.chargeTypeSelectorRef}
                    id="chargeTypeSelector"
                    onAddCallback={this.onAddChargeTypesCallback}
                />

                <ChargeTypeGroupSelect
                    ref={this.chargeTypeGroupSelectorRef}
                    id="chargeTypeGroupSelector"
                    onAddCallback={this.onAddChargeTypeGroupsCallback}
                />

                <SlideForm
                    loading={false}
                    show={this.state.showReorderWidget}
                    id="reorderChargeTypesWidget"
                    formIcon={faFileContract}
                    formTitle="Reorder Charge Types"
                    ref={this.chargeTypesWidgetRef}
                    onClose={() => {
                        this.setState((state) => (state.showReorderWidget = false, state));
                    }}
                >
                    <ReorderListWidget
                        entities={this.state.selectedSortableChargeTypes}
                        entityName="description"
                        customColors={false}
                        setUpdatedEntities={(items) => {
                            this.setState({ selectedSortableChargeTypes: items });
                        }}
                        ref={this.chargeTypesWidgetRef}
                        onSaveCallback={() => {
                            const { contractTemplate, selectedSortableChargeTypes } = { ...this.state };
                            let updated = [...selectedSortableChargeTypes];
                            updated = updated.map((x, index) => ({ ...x, ...{ order: index += 1 } }));
                            contractTemplate.chargeTypes = [...updated];
                            this.setState({
                                contractTemplate: { ...contractTemplate },
                                showReorderWidget: false,
                            });
                        }}
                    />
                </SlideForm>

                <PayScheduleSelect
                    ref={this.payScheduleSelectorRef}
                    id="payScheduleSelector"
                    onAddCallback={this.onAddPayScheduleCallback}
                />

                <USCountyGroupSelect
                    ref={this.uSCountyGroupSelectorRef}
                    id="uSCountyGroupSelector"
                    onAddCallback={this.onAddUSCountyGroupCallback}
                />
            </>
        );
    }
}
export default ContractTemplateForm;
